import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ToasterMessages } from '../../constants/constants';
import { CargoheatingCommonService } from '../../service/cargo-heating-service/cargoheating-common.service';
import { RequestFormService } from '../../service/requestForm/request-form.service';

@Component({
  selector: 'app-new-plan-popup',
  templateUrl: './new-plan-popup.component.html',
  styleUrls: ['./new-plan-popup.component.scss']
})
export class NewPlanPopupComponent implements OnInit {

  vessel_name: string = '';
  vessel_imo!: number;
  voyage_no: string = '';
  cargo_type: string = '';
  heating_type: string = '';
  segment: string = '';
  client: string = '';
  pic: string = '';   // ✅ PIC added
  isCustomClient: boolean = false;
  isLoadingClients: boolean = false;
  isSubmitting: boolean = false;
  /** Set on the first submit attempt, so empty required fields are highlighted from then on. */
  attemptedSubmit = false;

  toggleCustomClient(): void {
    this.isCustomClient = !this.isCustomClient;
  }

  segmentOptions = [
    { label: 'Handy(6W)', value: 'Handy(6W)' },
    { label: 'Handy(9W)', value: 'Handy(9W)' },
    { label: 'Intermediate / Coastal', value: 'Intermediate / Coastal' },
    { label: 'LR1 / Panamax', value: 'LR1 / Panamax' },
    { label: 'LR2 / Aframax', value: 'LR2 / Aframax' },
    { label: 'LR3 / Suezmax', value: 'LR3 / Suezmax' },
    { label: 'MR2', value: 'MR2' },
    { label: 'VLCC', value: 'VLCC' }
  ];

  clientOptions = [
    'Alghaf',
    'Bunge SA',
    'Clearlake Shipping',
    'Elite Tankships',
    'Litasco',
    'Northwell',
    'Stena Bulk'
  ];

  heatingTypeOptions = ['Heating Coils', 'Deck Heat Exchanger'];

  genericOilGradeOptions = [
    { value: 'Bio Diesel', label: 'Bio Diesel' },
    { value: 'Diesel MGO', label: 'Diesel MGO' },
    { value: 'Fame', label: 'Fame' },
    { value: 'Fuel Oil', label: 'Fuel Oil' },
    { value: 'Heavy Crude Oil', label: 'Heavy Crude Oil – ρ > 0.90 mt/m³ @ 15°C' },
    { value: 'Light Crude Oil', label: 'Light Crude Oil – ρ < 0.90 mt/m³ @ 15°C' },
    { value: 'Lube Oil', label: 'Lube Oil' },
    { value: 'Mineral Oil', label: 'Mineral Oil' },
    { value: 'Veg Oil', label: 'Veg Oil' },
    { value: 'VGO', label: 'VGO' }
  ];

  constructor(
    private dialogRef: MatDialogRef<NewPlanPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private toastr: ToastrService,
    private commonService: CargoheatingCommonService,
    private requestFormService: RequestFormService
  ) { }

  get isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }

  get selectPanelClass(): string[] {
    return this.isDarkMode ? ['npp-select-panel', 'npp-select-panel--dark'] : ['npp-select-panel'];
  }

  get hasMissingFields(): boolean {
    return [
      this.vessel_name,
      this.vessel_imo,
      this.voyage_no,
      this.cargo_type,
      this.heating_type,
      this.segment,
      this.client,
      this.pic,
    ].some((value) => this.isMissing(value));
  }

  isMissing(value: unknown): boolean {
    return this.attemptedSubmit && (value === null || value === undefined || String(value).trim() === '');
  }

  ngOnInit(): void {
    this.sortAllStaticOptions();

    if (this.data) {
      this.vessel_name = this.data.vessel_name || '';
      this.vessel_imo = this.data.vessel_imo || null;
      this.voyage_no = this.data.voyage_no || '';
      this.cargo_type = this.data.cargo_type || '';
      this.heating_type = this.data.heating_type || '';
      this.segment = this.data.segment || '';
      this.client = this.data.client || '';
      this.pic = this.data.pic || '';     // ✅ auto-populate PIC

      if (this.data.clientOptions && Array.isArray(this.data.clientOptions) && this.data.clientOptions.length > 0) {
        this.clientOptions = [...this.data.clientOptions].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      } else {
        this.loadClients();
      }
    } else {
      this.loadClients();
    }
  }

  private sortAllStaticOptions(): void {
    this.segmentOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    this.clientOptions.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    this.genericOilGradeOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }

  loadClients(): void {
    this.isLoadingClients = true;
    this.commonService.getAllClients().subscribe({
      next: (res: any) => {
        this.isLoadingClients = false;
        const clients = this.parseClientsResponse(res);
        if (clients.length > 0) {
          this.clientOptions = clients.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        }
      },
      error: (err: any) => {
        this.isLoadingClients = false;
        console.error('Error fetching clients in NewPlanPopupComponent:', err);
      }
    });
  }

  private parseClientsResponse(res: any): string[] {
    let list: any[] = [];
    if (Array.isArray(res)) {
      list = res;
    } else if (res && typeof res === 'object') {
      list = res.clients || res.data || res.client_names || res.result || [];
    }
    if (!Array.isArray(list)) return [];
    return list.map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        return item.client_name || item.name || item.client || item.client_name_str || String(item);
      }
      return String(item);
    }).filter(Boolean);
  }

  submitDateTime(confirm: boolean): void {
    if (!confirm) {
      this.dialogRef.close();
      return;
    }

    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    // ✅ Validation includes PIC
    if (
      !this.vessel_name ||
      !this.vessel_imo ||
      !this.voyage_no ||
      !this.cargo_type ||
      !this.heating_type ||
      !this.segment ||
      !this.client ||
      !this.pic
    ) {
      this.toastr.info(ToasterMessages.FormValidation);
      return;
    }

    // ✅ FINAL PAYLOAD (DISPLAY VALUES ONLY)
    const payload = {
      vessel_name: this.vessel_name,
      vessel_imo: Number(this.vessel_imo),
      voyage_no: this.voyage_no,
      cargo_type: this.cargo_type,
      heating_type: this.heating_type,
      segment: this.segment,
      client: this.client,
      pic: this.pic          // ✅ PIC sent
    };

    console.log('Create Heat Plan Payload:', payload);
    this.isSubmitting = true;

    this.commonService.createHeatPlan(payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toastr.success('Heat Plan created successfully');
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.isSubmitting = false;
        console.error(err);
        this.toastr.error('Failed to create Heat Plan');
      }
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
